import type { Project } from '@awork/features/project/models/project.model'
import type { Permissions, UserPermissions } from '@awork/features/workspace/models/permissions.model'
import { AccessLevels, Feature, Features } from '@awork/features/workspace/models/permissions.model'
import type { ProjectMember } from '@awork/features/project/models/project-member.model'
import type { Team } from '@awork/features/team/models/team.model'

/**
 * Determines if the current user is allowed to use a feature according to the project role permissions
 * @param {UserPermissions} userPermissions
 * @param {Features[]} features
 * @param {AccessLevels} accessLevel
 * @param {boolean} [redirect=false]
 * @param {Project} project
 * @param {CallableFunction} notAllowedAction
 * @returns {boolean} True if is allowed, notAllowedAction otherwise
 */
export function checkProjectRolePermissions(
  userPermissions: UserPermissions,
  features: Features[],
  accessLevel: AccessLevels,
  redirect = false,
  project: Project,
  notAllowedAction?: CallableFunction
): boolean {
  if (userPermissions.projectRolesPermissions?.length && project?.members?.length) {
    // Check if the current user is member of the project
    const userMember = getCurrentUserProjectMember(project, userPermissions)

    if (userMember) {
      // Look for user's permissions for the role in the project
      const projectRolePermissions = userPermissions.projectRolesPermissions.find(
        projectRolePermission => projectRolePermission.projectRoleId === userMember.projectRoleId
      )

      // If the user has permissions for the role, check if has the required permissions
      if (projectRolePermissions) {
        return checkPermissions(projectRolePermissions.permissions, features, accessLevel, redirect, notAllowedAction)
      }
    }
  }

  if (notAllowedAction) {
    notAllowedAction(redirect)
  }

  return false
}

/**
 * Check if the user has the permissions needed for certain features
 * @param {Permissions[]} userPermissions
 * @param {Features[]} features
 * @param {AccessLevels} accessLevel
 * @param {boolean} redirect
 * @param {CallableFunction} notAllowedAction
 * @returns {boolean}
 */
export function checkPermissions(
  userPermissions: Permissions[],
  features: Features[],
  accessLevel: AccessLevels,
  redirect: boolean,
  notAllowedAction?: CallableFunction
): boolean {
  // search for these features in the user's permissions
  const permissions = userPermissions.filter(permission => {
    // Filter the features array to only include features that the user has permissions for
    const featuresFiltered = Feature.features.filter(permissionFeature => features.includes(permissionFeature.id))
    // Find a feature that matches the user's permission
    const featureFound = featuresFiltered.find(feature => feature.value === permission.feature)

    // If a matching feature is found, the user is allowed
    return !!featureFound
  })

  if (permissions) {
    const allowed = permissions.some(permission => {
      if (accessLevel === AccessLevels.Manage) {
        // if 'Manage' is required, only 'Manage' is accepted
        return permission.accessLevels.includes(AccessLevels.Manage)
      } else if (accessLevel === AccessLevels.Read) {
        // if 'Read' is required, 'Manage' and 'Read' are accepted
        return (
          permission.accessLevels.includes(AccessLevels.Manage) || permission.accessLevels.includes(AccessLevels.Read)
        )
      }

      if (notAllowedAction) {
        notAllowedAction(redirect)
      }

      return false
    })

    if (allowed) {
      return allowed
    }
  }

  return false
}

/**
 * Returns a project member if the user is a member of the project
 * @param {Project} project
 * @param {UserPermissions} userPermissions
 * @returns {ProjectMember}
 * @private
 */
export function getCurrentUserProjectMember(project: Project, userPermissions: UserPermissions): ProjectMember {
  return project.members?.find(member => member.userId === userPermissions.userId)
}

/**
 * Checks if user belongs to the project (via project member or team)
 * @param {Project} project
 * @param {Team[]} ownTeams
 * @param {UserPermissions} userPermissions
 * @returns {boolean}
 */
export function hasProjectOwnTeamPermissions(
  project: Project,
  ownTeams: Team[],
  userPermissions: UserPermissions
): boolean {
  if (!project) {
    return false
  }

  const projectMember = getCurrentUserProjectMember(project, userPermissions)

  if (projectMember) {
    return true
  }

  const ownTeamsIds = ownTeams?.map(team => team.id) || []

  // Returns whether the user belong to any team of the given project
  return project.teams?.some(team => ownTeamsIds.includes(team.id))
}

/**
 * Builds a key that changes when budget access through Project times changes.
 * @param {UserPermissions | null} permissions - Signed permissions.
 * @returns {string | null} The key, or null before permissions are loaded.
 */
export function getProjectBudgetAccessKey(permissions: UserPermissions | null): string | null {
  if (!permissions?.userId) {
    return null
  }

  if (permissions.bpe !== true) {
    return JSON.stringify([permissions.userId, false])
  }

  const feature = Feature.getFeatureStringByEnum(Features.ProjectTimetracking)
  /**
   * Checks whether a permission list grants Project times Read.
   * @param {Permissions[] | undefined} list - The permission list.
   * @returns {boolean} True when Read or Manage is granted.
   */
  const grantsRead = (list?: Permissions[]): boolean =>
    !!list?.some(
      permission =>
        permission.feature === feature &&
        (permission.accessLevels?.includes(AccessLevels.Read) || permission.accessLevels?.includes(AccessLevels.Manage))
    )
  const roleIds = (permissions.projectRolesPermissions ?? [])
    .filter(role => !grantsRead(role.permissions))
    .map(role => role.projectRoleId)
    .sort()

  return JSON.stringify([permissions.userId, true, !!permissions.isAdmin, grantsRead(permissions.permissions), roleIds])
}
